import React from 'react';
import { reportCrashToServer } from '../services/crashReporter';
import { diagnosticText, diagnosticUrl } from '../services/diagnosticText';
import { hardReloadForUpdatedAssets, isAssetLoadFailure } from '../services/assetLoadRecovery';

function buildErrorDigest(error, errorInfo) {
    const message = String(error?.message || 'Error desconocido');
    const stack = String(error?.stack || '').split('\n').slice(0, 4).join(' | ');
    const componentStack = String(errorInfo?.componentStack || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 4)
        .join(' | ');

    return diagnosticText([message, stack, componentStack].filter(Boolean).join(' || '));
}

function getAppMeta() {
    const now = new Date().toISOString();
    const href = typeof window !== 'undefined' ? diagnosticUrl(window.location.href) : 'n/a';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a';
    const language = typeof navigator !== 'undefined' ? navigator.language : 'n/a';
    const platform = typeof navigator !== 'undefined' ? navigator.platform : 'n/a';
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    const appVersion = String(import.meta.env.VITE_APP_VERSION || 'desconocida');

    return {
        now,
        href,
        userAgent,
        language,
        platform,
        online,
        appVersion,
    };
}

function buildSupportCode(isoDate) {
    const compact = String(isoDate || '').replace(/[-:TZ.]/g, '').slice(0, 14);
    return `CV-ERR-${compact || 'UNKNOWN'}`;
}

function buildSupportReport(errorDigest) {
    const meta = getAppMeta();
    const supportCode = buildSupportCode(meta.now);

    const lines = [
        `Codigo: ${supportCode}`,
        `Fecha: ${meta.now}`,
        `VersionApp: ${meta.appVersion}`,
        `Online: ${meta.online ? 'si' : 'no'}`,
        `URL: ${meta.href}`,
        `Idioma: ${meta.language}`,
        `Plataforma: ${meta.platform}`,
        `UserAgent: ${meta.userAgent}`,
        `Error: ${errorDigest || 'sin detalle'}`,
    ];

    return {
        supportCode,
        reportText: lines.join('\n'),
    };
}

class AppCrashBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            errorDigest: '',
            supportCode: '',
            reportText: '',
            copyStatus: '',
        };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        const errorDigest = buildErrorDigest(error, errorInfo);
        const { supportCode, reportText } = buildSupportReport(errorDigest);
        this.setState({ errorDigest, supportCode, reportText });
        console.error('AppCrashBoundary caught error:', error, errorInfo);

        const sendReport = () => reportCrashToServer({
            support_code: supportCode,
            report_text: reportText,
            error_digest: errorDigest,
            url: typeof window !== 'undefined' ? window.location.href : null,
            app_version: String(import.meta.env.VITE_APP_VERSION || 'desconocida'),
            channel: 'react-boundary',
            context: {
                component_stack: String(errorInfo?.componentStack || '').slice(0, 8000),
            },
        });

        if (isAssetLoadFailure(error)) {
            void hardReloadForUpdatedAssets().then((recovered) => {
                if (!recovered) void sendReport();
            });
            return;
        }

        void sendReport();
    }

    handleReload = () => {
        window.location.reload();
    };

    handleHardRecovery = async () => {
        try {
            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map((registration) => registration.unregister()));
            }

            if ('caches' in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((key) => caches.delete(key)));
            }
        } catch {
            // Best effort
        }

        window.location.reload();
    };

    handleCopyReport = async () => {
        const text = this.state.reportText || 'Sin datos de diagnóstico';

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                this.setState({ copyStatus: 'Reporte copiado al portapapeles.' });
                return;
            }
        } catch {
            // Fallback abajo si Clipboard API falla.
        }

        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            this.setState({ copyStatus: 'Reporte copiado al portapapeles.' });
        } catch {
            this.setState({ copyStatus: 'No se pudo copiar automáticamente. Seleccioná el texto y copialo manualmente.' });
        }
    };

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <div style={{
                minHeight: '100dvh',
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
                padding: 20,
            }}>
                <div style={{
                    width: 'min(560px, 100%)',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    boxShadow: '0 12px 36px rgba(15, 23, 42, 0.12)',
                    padding: 20,
                    color: '#0f172a',
                    fontFamily: 'Inter, system-ui, sans-serif',
                }}>
                    <h1 style={{ margin: 0, fontSize: 20, lineHeight: 1.25 }}>No se pudo cargar la aplicación</h1>
                    <p style={{ marginTop: 10, marginBottom: 0, color: '#334155' }}>
                        Se detectó un error inesperado en la interfaz. Podés intentar reiniciar o hacer una recuperación completa de caché.
                    </p>

                    <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={this.handleReload}
                            style={{
                                border: 'none',
                                background: '#2563eb',
                                color: '#fff',
                                borderRadius: 8,
                                padding: '10px 14px',
                                cursor: 'pointer',
                                fontWeight: 600,
                            }}
                        >
                            Reiniciar
                        </button>
                        <button
                            type="button"
                            onClick={this.handleHardRecovery}
                            style={{
                                border: '1px solid #cbd5e1',
                                background: '#fff',
                                color: '#0f172a',
                                borderRadius: 8,
                                padding: '10px 14px',
                                cursor: 'pointer',
                                fontWeight: 600,
                            }}
                        >
                            Recuperación completa
                        </button>
                        <button
                            type="button"
                            onClick={this.handleCopyReport}
                            style={{
                                border: '1px solid #cbd5e1',
                                background: '#f8fafc',
                                color: '#0f172a',
                                borderRadius: 8,
                                padding: '10px 14px',
                                cursor: 'pointer',
                                fontWeight: 600,
                            }}
                        >
                            Copiar diagnóstico
                        </button>
                    </div>

                    <div style={{ marginTop: 14, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
                        <div style={{ fontSize: 13, color: '#334155' }}>Código para soporte:</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>
                            {this.state.supportCode || 'CV-ERR-UNKNOWN'}
                        </div>
                        {this.state.copyStatus ? (
                            <div style={{ fontSize: 12, color: '#2563eb', marginTop: 6 }}>{this.state.copyStatus}</div>
                        ) : null}
                    </div>

                    {this.state.reportText ? (
                        <textarea
                            readOnly
                            value={this.state.reportText}
                            style={{
                                marginTop: 10,
                                width: '100%',
                                minHeight: 120,
                                resize: 'vertical',
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 12,
                                color: '#334155',
                                background: '#ffffff',
                            }}
                        />
                    ) : null}

                    {this.state.errorDigest ? (
                        <details style={{ marginTop: 14 }}>
                            <summary style={{ cursor: 'pointer', color: '#475569' }}>Detalle técnico</summary>
                            <pre style={{
                                marginTop: 8,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 12,
                                color: '#334155',
                            }}>
                                {this.state.errorDigest}
                            </pre>
                        </details>
                    ) : null}
                </div>
            </div>
        );
    }
}

export default AppCrashBoundary;
